import type { Key, Translation, TranslationStatus } from '@/lib/api';

export type StatusFilter = TranslationStatus | 'missing';

export const ALL_STATUSES: StatusFilter[] = [
  'missing',
  'empty',
  'machine',
  'fuzzy',
  'reviewed',
  'approved',
];

export interface EditorFilters {
  query: string;
  statuses: Set<StatusFilter>;
  namespaces: Set<string>;
}

export const emptyFilters = (): EditorFilters => ({
  query: '',
  statuses: new Set<StatusFilter>(),
  namespaces: new Set<string>(),
});

/** Effective status for a key/lang pair — `missing` if no translation exists. */
export function rowStatus(t: Translation | undefined): StatusFilter {
  if (!t) return 'missing';
  if (!t.value && t.status !== 'fuzzy') return 'empty';
  return t.status;
}

export function translationByKey(translations: Translation[] | undefined): Map<string, Translation> {
  const m = new Map<string, Translation>();
  for (const t of translations ?? []) m.set(t.keyId, t);
  return m;
}

/**
 * Filter a key list by the user's filters. Match modes:
 *
 *  - query: case-insensitive substring of key, namespace, sourceText, or value.
 *  - statuses: row's effective status must be in the set (empty set = no filter).
 *  - namespaces: key's namespace must be in the set (empty set = no filter).
 */
export function filterKeys(
  keys: Key[],
  translations: Translation[] | undefined,
  filters: EditorFilters,
): Key[] {
  const byKey = translationByKey(translations);
  const q = filters.query.trim().toLowerCase();
  const out: Key[] = [];
  for (const k of keys) {
    if (filters.namespaces.size > 0 && !filters.namespaces.has(k.namespace ?? '')) continue;
    const t = byKey.get(k.id);
    if (filters.statuses.size > 0 && !filters.statuses.has(rowStatus(t))) continue;
    if (q) {
      const hay = [
        k.key,
        k.namespace ?? '',
        k.sourceText,
        k.context ?? '',
        k.description ?? '',
        t?.value ?? '',
      ]
        .join('\n')
        .toLowerCase();
      if (!hay.includes(q)) continue;
    }
    out.push(k);
  }
  return out;
}

export function uniqueNamespaces(keys: Key[]): string[] {
  const set = new Set<string>();
  for (const k of keys) set.add(k.namespace ?? '');
  return Array.from(set).sort();
}

/** Counts per status across the given keys (for the filter chips' badge). */
export function statusCounts(
  keys: Key[],
  translations: Translation[] | undefined,
): Record<StatusFilter, number> {
  const byKey = translationByKey(translations);
  const counts: Record<StatusFilter, number> = {
    missing: 0,
    empty: 0,
    machine: 0,
    fuzzy: 0,
    reviewed: 0,
    approved: 0,
  };
  for (const k of keys) counts[rowStatus(byKey.get(k.id))]++;
  return counts;
}

/** Pick the next key id given the current selection and the filtered window. */
export function nextKeyId(filtered: Key[], current: string | null, delta: 1 | -1): string | null {
  if (filtered.length === 0) return null;
  if (current === null) return filtered[0]?.id ?? null;
  const idx = filtered.findIndex((k) => k.id === current);
  if (idx === -1) return filtered[0]?.id ?? null;
  const nextIdx = Math.min(filtered.length - 1, Math.max(0, idx + delta));
  return filtered[nextIdx]?.id ?? null;
}

/** Status → human label + Tailwind tone classes (kept in one place for reuse). */
export const STATUS_LABEL: Record<StatusFilter, string> = {
  missing: 'Missing',
  empty: 'Empty',
  machine: 'AI',
  fuzzy: 'Fuzzy',
  reviewed: 'Reviewed',
  approved: 'Approved',
};

export const STATUS_TONE: Record<StatusFilter, string> = {
  missing: 'bg-muted text-muted-foreground',
  empty: 'bg-muted text-muted-foreground',
  machine: 'bg-primary/10 text-primary',
  fuzzy: 'bg-warning/15 text-warning',
  reviewed: 'bg-primary/10 text-primary',
  approved: 'bg-positive/15 text-positive',
};
